import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { TicketStatus } from '../../../models/Support/SupportEnums';
import { useUpdateTicketStatusMutation } from '../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { CheckGlyph, EyeGlyph, EyeOffGlyph, ForwardGlyph, PersonSwapGlyph, ReopenGlyph, SpamGlyph } from './inboxIcons';
import { styles } from './styles';
import { getComplexityInfo } from './supportTicketPresentation';
import { useSupportTicketActivityVisibility } from './useSupportTicketActivityVisibility';

interface TicketTopBarProps {
	ticket: ISupportTicket;
	/** Called when the user selects Forward. */
	onForward?: () => void;
	/** Called when the agent wants to change the customer/contact attached to the ticket. */
	onChangeCustomer?: () => void;
	/** Called after the ticket leaves the agent's queue (closed / marked spam) — parent advances to the next ticket. */
	onTicketLeftView?: () => void;
}

export const TicketTopBar: React.FC<TicketTopBarProps> = ({
	ticket,
	onForward,
	onChangeCustomer,
	onTicketLeftView,
}) => {
	const [showSpamConfirm, setShowSpamConfirm] = React.useState(false);
	const [showReopenConfirm, setShowReopenConfirm] = React.useState(false);
	const [showTicketActivity, setShowTicketActivity] = useSupportTicketActivityVisibility();
	// Support has no MutationCache/toast, so status-change failures must surface inline.
	const [mutationError, setMutationError] = React.useState<string | null>(null);

	const updateStatus = useUpdateTicketStatusMutation(ticket.id);
	const activityToggleLabel = showTicketActivity ? 'Hide activity' : 'Show activity';
	const customerActionLabel = ticket.contactEmail?.trim() ? 'Change customer' : 'Add customer';
	const isClosed = ticket.status === TicketStatus.Closed;
	const feedback = (failMessage: string, onSuccess?: () => void) => ({
		onSuccess: () => {
			setMutationError(null);
			onSuccess?.();
		},
		onError: (error: unknown) => setMutationError(getApiErrorMessage(error, failMessage)),
	});

	return (
		<>
			<div className={css(styles.topBarInline)}>
				<div className={css(styles.topBarLeft)}>
					<div className={css(styles.topBarTitleRow)}>
						<span className={css(styles.ticketNumber)}>#{ticket.number}</span>
						<span className={css(styles.ticketSubject)}>{ticket.subject}</span>
						{ticket.complexity != null &&
							(() => {
								const info = getComplexityInfo(ticket.complexity);
								return (
									<span
										className={css(styles.aiScoreBadge, info.style)}
										title={ticket.complexityReasoning || ticket.aiReasoning || undefined}
									>
										{info.label} ({ticket.complexity})
									</span>
								);
							})()}
					</div>
				</div>
				<div className={css(styles.topBarActions)}>
					{mutationError && <span className={css(styles.actionError)}>{mutationError}</span>}
					<Button
						variant='primary'
						size='default'
						onClick={() => {
							if (isClosed) {
								setShowReopenConfirm(true);
							} else {
								updateStatus.mutate(TicketStatus.Closed, feedback('Failed to close ticket.', onTicketLeftView));
							}
						}}
						isLoading={updateStatus.isPending}
						title={isClosed ? 'Reopen' : 'Close'}
						styles={[styles.actionPrimaryBtn]}
					>
						{isClosed ? <ReopenGlyph size={16} /> : <CheckGlyph size={16} />}
						{isClosed ? 'Reopen' : 'Close'}
					</Button>
					<Button
						type='button'
						variant='outline'
						size='icon'
						onClick={() => setShowTicketActivity(show => !show)}
						title={activityToggleLabel}
						aria-label={activityToggleLabel}
						aria-pressed={showTicketActivity}
						styles={[styles.actionIconBtn, ...(showTicketActivity ? [] : [styles.actionIconBtnActive])]}
					>
						{showTicketActivity ? <EyeGlyph size={18} /> : <EyeOffGlyph size={18} />}
					</Button>
					<Button
						variant='outline'
						size='icon'
						onClick={() => onForward?.()}
						title='Forward'
						aria-label='Forward'
						styles={[styles.actionIconBtn]}
					>
						<ForwardGlyph size={18} />
					</Button>
					<Button
						variant='outline'
						size='icon'
						onClick={() => setShowSpamConfirm(true)}
						title='Mark as spam'
						aria-label='Mark as spam'
						styles={[styles.actionIconBtn, styles.actionIconBtnDanger]}
					>
						<SpamGlyph size={18} />
					</Button>
					<Button
						variant='outline'
						size='icon'
						onClick={() => onChangeCustomer?.()}
						title={customerActionLabel}
						aria-label={customerActionLabel}
						styles={[styles.actionIconBtn]}
					>
						<PersonSwapGlyph size={18} />
					</Button>
				</div>
			</div>
			{showSpamConfirm && (
				<ConfirmModal
					title='Mark as Spam'
					message='Are you sure you want to mark this ticket as spam? The ticket will be moved to the Spam folder.'
					confirmLabel='Mark as Spam'
					variant='danger'
					onConfirm={() => {
						setShowSpamConfirm(false);
						updateStatus.mutate(TicketStatus.Spam, feedback('Failed to mark ticket as spam.', onTicketLeftView));
					}}
					onCancel={() => setShowSpamConfirm(false)}
				/>
			)}
			{showReopenConfirm && (
				<ConfirmModal
					title='Reopen Ticket'
					message='Reopen this ticket and move it back to Active?'
					confirmLabel='Reopen'
					onConfirm={() => {
						setShowReopenConfirm(false);
						updateStatus.mutate(TicketStatus.Open, feedback('Failed to reopen ticket.'));
					}}
					onCancel={() => setShowReopenConfirm(false)}
				/>
			)}
		</>
	);
};
